import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import CircleIcon from "@mui/icons-material/Circle";
import Typography from "@mui/material/Typography";

interface SentenceTimelineProps {
  minPercent: number;
  maxPercent: number;
  jailTimeMonths: number; // in months
  totalSentence: number; // in months
}

export function SentenceTimeline({
  totalSentence,
  minPercent,
  maxPercent,
  jailTimeMonths,
}: SentenceTimelineProps) {
  const myHeight: number = 550;
  const myHeightString: string = myHeight.toString() + "px";

  const minSentence: number = (minPercent / 100) * totalSentence;
  const maxSentence: number = (maxPercent / 100) * totalSentence;

  const jailLineLength: string =
    ((jailTimeMonths / totalSentence) * myHeight).toString() + "px";
  const startMinLineLength =
    (((minSentence - jailTimeMonths) / totalSentence) * myHeight).toString() +
    "px";
  const minMaxLineLength: string =
    (((maxPercent - minPercent) / 100) * myHeight).toString() + "px";
  const maxReleaseLineLength =
    (((100 - maxPercent) / 100) * myHeight).toString() + "px";

  return (
    // {/* start of timeline */}
    <Timeline
      sx={{
        height: myHeightString,
        pl: 0,
        [`& .${timelineOppositeContentClasses.root}`]: {
          flex: 0.1,
        },
      }}
    >
      {/* box for line */}

      {/* jail time */}
      {jailTimeMonths === 0 ? null : (
        <TimelineItem
          sx={{
            minHeight: "10px",
            width: "100px",
            minWidth: "100px",
          }}
        >
          <TimelineOppositeContent
            sx={{
              color: "#595959",
              fontSize: "small",
              width: "100px",
              minWidth: "100px",
              p: "4px 0",
            }}
          >
            JAIL
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot
              sx={{
                backgroundColor: "transparent",
                boxShadow: "none",
                p: 0,
                m: 0,
              }}
            >
              <HorizontalRuleIcon sx={{ color: "#595959", opacity: "0.8" }} />
            </TimelineDot>
            <TimelineConnector
              sx={{
                width: "0px",
                border: "4px dashed #B30000",
                background: "none",
                minHeight: "0px",
                height: jailLineLength,
              }}
            />
          </TimelineSeparator>
        </TimelineItem>
      )}

      {/* jail to prison start */}
      <TimelineItem
        sx={{
          width: "100px",
          minWidth: "100px",
        }}
      >
        <TimelineOppositeContent
          sx={{
            color: "#595959",
            fontSize: "small",
            width: "100px",
            minWidth: "100px",
            p: "4px 0",
          }}
        >
          PRISON
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              p: 0,
              m: 0,
            }}
          >
            <HorizontalRuleIcon sx={{ color: "#595959", opacity: "0.8" }} />
          </TimelineDot>
          <TimelineConnector
            sx={{
              width: "8px",
              backgroundColor: "#B30000",
              borderRadius: "1px",
              height: startMinLineLength,
            }}
          />
        </TimelineSeparator>
      </TimelineItem>

      {/* prison start to min sentence */}
      <TimelineItem>
        {!isNaN(minSentence) && (
          <TimelineOppositeContent
            sx={{
              fontSize: "medium",
              color: "#B30000",
              fontWeight: "700",
              width: "100px",
              minWidth: "100px",
              p: 0,
              mt: "-4px",
              lineHeight: 1.1,
            }}
          >
            Parole Eligibility
          </TimelineOppositeContent>
        )}
        <TimelineSeparator>
          <TimelineDot
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              p: "0 6px",
              m: 0,
            }}
          >
            <CircleIcon sx={{ color: "#B30000", width: "12px", height: "12px" }} />
          </TimelineDot>
          <TimelineConnector
            sx={{
              width: "8px",
              backgroundColor: "#B30000",
              borderRadius: "1px",
              height: isNaN(minSentence) ? "250px" : minMaxLineLength,
            }}
          />
        </TimelineSeparator>
        <TimelineContent
          sx={{
            mt: { lg: "-16px", xs: "-12px" },
          }}
        >
          <Typography
            sx={{
              color: "black",
              fontSize: { lg: "20px", xs: "14px" },
              fontWeight: "600",
              lineHeight: 1.1,
            }}
          >
            {Math.floor(minSentence / 12)} years, {Math.floor(minSentence % 12)}{" "}
            months served
          </Typography>
          <Typography
            sx={{
              color: "#595959",
              fontSize: { lg: "20px", xs: "14px" },
              whiteSpace: { lg: "nowrap" },
              lineHeight: 1.1,
            }}
          >
            {minPercent}% of sentence
          </Typography>
        </TimelineContent>
      </TimelineItem>

      {/* min to max */}
      <TimelineItem>
        <TimelineOppositeContent
          sx={{
            color: "#B30000",
            fontSize: "medium",
            fontWeight: "700",
            width: "100px",
            minWidth: "100px",
            p: 0,
            mt: "-4px",
            lineHeight: 1.1,
          }}
        >
          {maxPercent === 100 ? "Discharge" : "Conditional Discharge"}
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              p: "0 6px",
              m: 0,
            }}
          >
            <CircleIcon sx={{ color: "#B30000", width: "12px", height: "12px" }} />
          </TimelineDot>
          {maxPercent !== 100 && (
            <TimelineConnector
              sx={{
                width: "8px",
                border: "4px dashed #B30000",
                background: "none",
                height: isNaN(maxSentence) ? "250px" : maxReleaseLineLength,
              }}
            />
          )}
        </TimelineSeparator>
        {!isNaN(maxSentence) && (
          <TimelineContent
            sx={{
              mt: { lg: "-16px", xs: "-12px" },
            }}
          >
            <Typography
              sx={{
                color: "black",
                fontSize: { lg: "20px", xs: "14px" },
                fontWeight: "600",
                lineHeight: 1.1,
              }}
            >
              {Math.floor(maxSentence / 12)} years,{" "}
              {Math.floor(maxSentence % 12)} months served
            </Typography>
            <Typography
              sx={{
                color: "#595959",
                fontSize: { lg: "20px", xs: "14px" },
                whiteSpace: { lg: "nowrap" },
                lineHeight: 1.1,
              }}
            >
              {maxPercent}% of sentence
            </Typography>
          </TimelineContent>
        )}
      </TimelineItem>

      {/* Question about location of parole, and possibly hover */}

      {/* max to release */}
      {maxPercent !== 100 && (
        <TimelineItem
          sx={{
            width: "100px",
            minWidth: "100px",
          }}
        >
          <TimelineOppositeContent
            sx={{
              color: "#595959",
              fontSize: "small",
              width: "100px",
              minWidth: "100px",
              p: "4px 0",
            }}
          >
            DISCHARGE
          </TimelineOppositeContent>
          <TimelineDot
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              p: 0,
              m: 0,
            }}
          >
            <HorizontalRuleIcon sx={{ color: "#595959", opacity: "0.8" }} />
          </TimelineDot>
        </TimelineItem>
      )}
    </Timeline>
  );
}
